import { readonly } from 'vue'
import userApiClient from '@/api-client/user'
import paymentApiClient from '@/api-client/payment'
import {
  LoginParameterInterface,
  RepaymentScheduleInterface,
  ZeroWidgetPropsInterface,
  ScheduleInterface,
  CardDetailsInterface,
  DownPaymentInterface,
  WalletDetailsInterface
} from '@/models/composables'
import { getBaseUrl } from '@/composables/useUtilities/index'

import checkoutState from './initialState'
import { createPersistedState } from '../useUtilities'
import { CheckoutStateInterface } from '@/models/global-state'
const { zeroWidgetProps, repaymentSchedule, redirectUrl } = checkoutState
const state: CheckoutStateInterface = createPersistedState(checkoutState, {
  zeroWidgetProps,
  repaymentSchedule,
  redirectUrl
})
Object.seal(state)

/*===============================================================================
PRIVATE METHODS
================================================================================= */
const apiCallSuccess = (response: unknown) => {
  state.apiCallTracker.response = response
  state.apiCallTracker.isComplete = true
  state.apiCallTracker.isSuccess = true
  state.apiCallTracker.inProgress = false
  state.apiCallTracker.isError = false
}

const apiCallError = () => {
  state.apiCallTracker.isError = true
  state.apiCallTracker.isComplete = true
  state.apiCallTracker.inProgress = false
}

const setComponent = (component: string) => {
  state.component = component
}

const setErrorMessage = (message: string) => {
  state.errorMessage = message
}

const setBaseUrl = (apiKey: string) => {
  const apiKeyPrefix = apiKey.substring(0, 4)
  const baseURL = getBaseUrl(apiKeyPrefix)
  state.baseURL = baseURL
}

const setZeroWidgetProp = (props: ZeroWidgetPropsInterface) => {
  state.zeroWidgetProps = props
}

const unAuthorisedUser = (error: any) => {
  const errorResponse = error?.response
  const statusCode = errorResponse?.status
  if (statusCode === 401) {
    setCheckAccess(false)
    setComponent('un-authorised')
  }
}

/*===============================================================================
USER METHODS
================================================================================= */
const setUserToken = (token: string) => {
  state.userToken = token
}

const setTokenExpiry = (status: boolean) => {
  state.isTokenExpired = status
}

const login = async (userInfo: LoginParameterInterface) => {
  const runtimeConfigs = {
    baseUrl: state.baseURL,
    userToken: state.userToken,
    runtimeHeaderParams: {
      'x-merchant-key': state.zeroWidgetProps.apiKey,
      'x-merchant-id': state.zeroWidgetProps.merchantId
    }
  }
  try {
    state.apiCallTracker.inProgress = true
    state.widgetLoginDetails = userInfo
    const { response } = await userApiClient.loginUser(userInfo, runtimeConfigs)
    if (!response.data.withinSpendLimit) {
      state.component = 'exceed-limit'
    } else {
      state.component = 'payment-plan'
    }
    const token = response.data.token
    setUserToken(token)
    apiCallSuccess(response)
  } catch (error: any) {
    const errorResponse = error.response
    const errorMessage = errorResponse?.data?.response?.message
    state.errorMessage = errorResponse?.data?.response?.message
    unAuthorisedUser(error)
    if (errorMessage === 'INCOMPLETE_REGISTRATION') {
      state.component = 'incomplete-registration'
    } else {
      state.emailOrPasswordError = true
    }
    apiCallError()
  }
}

/*===============================================================================
MERCHANT METHODS
================================================================================= */
const setCustomerPortal = (environment: string) => {
  if (environment === 'live') {
    state.customerPortaAddress = 'https://zero-app.getcarbon.co'
    return
  }
  state.customerPortaAddress = 'https://zero-staging.getcarbon.co'
}

const setCheckAccess = async (status: boolean) => {
  state.checkAccess = status
}

/*===============================================================================
PURCHASE AND PAYMENT METHODS
================================================================================= */
const setRepaymentSchedule = (schedule: ScheduleInterface[]) => {
  state.repaymentSchedule = schedule
}
const setCardDetails = (cardDetails: CardDetailsInterface) => {
  state.cardDetails = cardDetails
}
const setWalletDetails = (walletDetails: WalletDetailsInterface) => {
  state.walletDetails = walletDetails
}

const setPaymentInstrumentType = (paymentInstrumentType: string) => {
  state.paymentInstrumentType = paymentInstrumentType
}

const getRepaymentSchedule = async (queryParams: RepaymentScheduleInterface) => {
  const runtimeConfigs = {
    baseUrl: state.baseURL,
    userToken: state.userToken,
    runtimeHeaderParams: {
      'x-merchant-key': state.zeroWidgetProps.apiKey,
      'x-merchant-id': state.zeroWidgetProps.merchantId
    }
  }
  try {
    state.apiCallTracker.inProgress = true
    const { response } = await paymentApiClient.getRepaymentSchedule(queryParams, runtimeConfigs)
    apiCallSuccess(response)
    return response
  } catch (error: any) {
    const errorResponse = error?.response
    state.errorMessage = errorResponse?.data?.response?.message
    unAuthorisedUser(error)
    console.error(error)
    apiCallError()
  }
}

const getCardDetails = async () => {
  const runtimeConfigs = {
    baseUrl: state.baseURL,
    userToken: state.userToken,
    runtimeHeaderParams: {
      'x-merchant-key': state.zeroWidgetProps.apiKey,
      'x-merchant-id': state.zeroWidgetProps.merchantId
    }
  }
  try {
    state.apiCallTracker.inProgress = true
    const { response } = await paymentApiClient.getCardDetails(runtimeConfigs)
    setCardDetails(response?.data?.card)
    setWalletDetails(response.data.wallet)
    state.checkCardAvailability = 'available'
    apiCallSuccess(response)
    return response
  } catch (error: any) {
    unAuthorisedUser(error)
    state.checkCardAvailability = 'unavailable'
    apiCallError()
  }
}

const makeDownPayment = async (payload: DownPaymentInterface) => {
  const runtimeConfigs = {
    baseUrl: state.baseURL,
    userToken: state.userToken,
    runtimeHeaderParams: {
      'x-merchant-key': state.zeroWidgetProps.apiKey,
      'x-merchant-id': state.zeroWidgetProps.merchantId
    }
  }
  try {
    state.apiCallTracker.inProgress = true
    const { response } = await paymentApiClient.makeDownPayment(payload, runtimeConfigs)
    state.redirectUrl = response.data?.redirectUrl
    state.component = 'success-modal'
    apiCallSuccess(response)
    return response
  } catch (error: any) {
    const errorResponse = error?.response
    unAuthorisedUser(error)
    !!errorResponse?.data && setComponent('failure-modal')
    apiCallError()
  }
}

export default {
  state: readonly(state),
  actions: {
    login,
    setCheckAccess,
    setTokenExpiry,
    getRepaymentSchedule,
    setZeroWidgetProp,
    setRepaymentSchedule,
    setComponent,
    getCardDetails,
    setCardDetails,
    makeDownPayment,
    setErrorMessage,
    setBaseUrl,
    setCustomerPortal,
    setPaymentInstrumentType
  }
}
